import { message as antdMessage, Modal, notification } from 'antd'
import type { ArgsProps } from 'antd/es/notification'

// Toast/bildirim için tek geçit. Ant Design notification/message yalnızca buradan
// import edilir. Aynı mesaj + tip 2.5sn içinde tekrarlanırsa yok sayılır (dedupe).

type ToastInput =
  | string
  | (Partial<ArgsProps> & { message: ArgsProps['message'] })

const DEDUPE_MS = 2500
const lastShown: Record<string, number> = {}

const dedupe = (key: string): boolean => {
  const now = Date.now()
  if (now - (lastShown[key] ?? 0) < DEDUPE_MS) return false
  lastShown[key] = now
  return true
}

const normalize = (
  input: ToastInput
): Partial<ArgsProps> & { message: ArgsProps['message'] } =>
  typeof input === 'string' ? { message: input } : input

const show = (
  variant: 'success' | 'error' | 'info' | 'warning',
  input: ToastInput
) => {
  const args = normalize(input)
  const key = `notify:${variant}:${String(args.message)}:${String(args.description ?? '')}`
  if (!dedupe(key)) return
  notification[variant]({
    placement: 'topRight',
    duration: 3,
    ...args,
  })
}

const flashShow = (
  variant: 'success' | 'error' | 'info' | 'warning',
  content: string
) => {
  const key = `flash:${variant}:${content}`
  if (!dedupe(key)) return
  void antdMessage[variant](content)
}

export const notify = {
  success: (input: ToastInput) => show('success', input),
  error: (input: ToastInput) => show('error', input),
  info: (input: ToastInput) => show('info', input),
  warning: (input: ToastInput) => show('warning', input),
}

/**
 * Anlık satır içi bildirim (antd `message`). Tam toast gerektirmeyen geçici
 * geri bildirimler için. Başlık + açıklama gerektiren durumlarda `notify.*` kullan.
 */
export const flash = {
  success: (content: string) => flashShow('success', content),
  error: (content: string) => flashShow('error', content),
  info: (content: string) => flashShow('info', content),
  warning: (content: string) => flashShow('warning', content),
}

type ConfirmInput = {
  title: string
  content?: string
  okText?: string
  cancelText?: string
  danger?: boolean
  onOk: () => void
}

/**
 * Onay diyaloğu için tek geçit (Ant Design `Modal.confirm` yalnızca buradan). Geri-dönüşü
 * olmayan / bilinçli karar gerektiren aksiyonlar için (ör. R-24 near-dupe onayı).
 */
export const confirm = ({
  title,
  content,
  okText = 'Onayla',
  cancelText = 'Vazgeç',
  danger = false,
  onOk,
}: ConfirmInput) => {
  Modal.confirm({
    title,
    content,
    okText,
    cancelText,
    okButtonProps: danger ? { danger: true } : undefined,
    onOk,
  })
}
